import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import {
  getMyCompareData,
  getMyCompareScaffold,
  type CompareMetricInfo,
  type MetricReference,
  type ParticipantCompareData,
  type ParticipantCompareScaffold,
} from '../api/sessionMetrics';
import { prettyMetric } from '../util/metricLabels';
import { SearchableSelect } from './SearchableSelect';

/**
 * "We are ready for your data" — the participant Compare empty state
 * (COMPARE-CONFIG). Shown when a participant has no sessions yet: it previews
 * the metrics they'll be measured on, each with the not-practice-ready /
 * practice-ready benchmark ticks, so a first-time participant sees where the
 * bar is before they've recorded anything.
 */

// Cohort key → tick label + colour (mirrors the scorecard / Compare view).
const COHORT_META: Record<string, { label: string; color: string }> = {
  not_practice_ready: { label: 'Not practice-ready', color: '#ab47bc' },
  novice: { label: 'Not practice-ready', color: '#ab47bc' },
  beginner: { label: 'Not practice-ready', color: '#ab47bc' },
  practice_ready: { label: 'Practice-ready', color: '#42a5f5' },
  trainee: { label: 'Practice-ready', color: '#42a5f5' },
  competent: { label: 'Practice-ready', color: '#42a5f5' },
  proficient: { label: 'Practice-ready', color: '#42a5f5' },
  expert: { label: 'Expert', color: '#66bb6a' },
  attending: { label: 'Expert', color: '#66bb6a' },
  faculty: { label: 'Expert', color: '#66bb6a' },
  mastery: { label: 'Expert', color: '#66bb6a' },
};
function resolveCohort(c: string): { label: string; color: string } {
  return (
    COHORT_META[c] ?? {
      label: c.replace(/_/g, ' ').replace(/\b\w/g, (x) => x.toUpperCase()),
      color: '#9e9e9e',
    }
  );
}

function fmtValue(v: number, unit: string | null): string {
  if (unit === 's') return `${Math.round(v)}s`;
  if (unit === '%') return `${Math.round(v * 100)}%`;
  return String(Number(v.toFixed(3)));
}

// You = orange (matches the scorecard); the comparison group = slate. Both
// distinct from the purple/blue reference ticks.
const YOU_COLOR = '#f57c00';
const OTHER_COLOR = '#455a64';

function betterSide(
  direction: string,
  you: number | null,
  other: number | null,
): 'you' | 'other' | null {
  if (direction === 'none' || you === null || other === null || you === other)
    return null;
  return (direction === 'higher' ? you > other : you < other) ? 'you' : 'other';
}

// Whole cohort first, then the experience bands junior → senior — a
// meaningful order, so the dropdown keeps it rather than alphabetizing.
const AGAINST_OPTIONS = [
  { value: 'cohort', label: 'Whole cohort' },
  { value: 'exp_0_2', label: '≤ 2 yr experience' },
  { value: 'exp_3_7', label: '3–7 yr experience' },
  { value: 'exp_8_plus', label: '8+ yr experience' },
];

const SECTIONS = [
  { key: 'mot', label: 'Motion (IMU)', match: (m: CompareMetricInfo) => m.modality !== 'eeg' },
  { key: 'cog', label: 'Cognitive (EEG)', match: (m: CompareMetricInfo) => m.modality === 'eeg' },
];

/** A worse→better track with the reference cohort ticks plus (when the
 *  participant has data) the You + comparison-group markers. */
function RefGauge({
  m,
  refs,
  you = null,
  other = null,
  otherLabel,
}: {
  m: CompareMetricInfo;
  refs: MetricReference[];
  you?: number | null;
  other?: number | null;
  otherLabel?: string;
}) {
  const directional = m.direction === 'higher' || m.direction === 'lower';
  const win = betterSide(m.direction, you, other);
  const vals = [
    ...refs.map((r) => r.value),
    ...(you !== null ? [you] : []),
    ...(other !== null ? [other] : []),
  ];
  let track: React.ReactNode = null;
  if (vals.length > 0) {
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo || Math.abs(hi) || 1) * 0.25;
    const dmin = lo - pad;
    const dmax = hi + pad;
    const span = dmax - dmin || 1;
    const rawPct = (v: number) =>
      Math.max(0, Math.min(100, ((v - dmin) / span) * 100));
    const pos = (v: number) =>
      m.direction === 'lower' ? 100 - rawPct(v) : rawPct(v);
    track = (
      <Box
        sx={{
          position: 'relative',
          height: 14,
          borderRadius: '7px',
          bgcolor: 'action.hover',
        }}
      >
        {refs.map((r) => {
          const { label, color } = resolveCohort(r.cohort);
          return (
            <Tooltip key={r.cohort} title={`${label}: ${fmtValue(r.value, m.unit)}`}>
              <Box
                sx={{
                  position: 'absolute',
                  top: -3,
                  bottom: -3,
                  left: `${pos(r.value)}%`,
                  transform: 'translateX(-50%)',
                  width: 6,
                  borderRadius: 1,
                  bgcolor: color,
                  boxShadow: (t) => `0 0 0 1.5px ${t.palette.background.paper}`,
                }}
              />
            </Tooltip>
          );
        })}
        {[
          { v: you, color: YOU_COLOR, label: 'You', isWin: win === 'you' },
          {
            v: other,
            color: OTHER_COLOR,
            label: otherLabel ?? 'Cohort',
            isWin: win === 'other',
          },
        ].map((marker) =>
          marker.v === null ? null : (
            <Tooltip
              key={marker.label}
              title={`${marker.label}: ${fmtValue(marker.v, m.unit)}`}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -5,
                  bottom: -5,
                  left: `${pos(marker.v)}%`,
                  transform: 'translateX(-50%)',
                  width: marker.isWin ? 11 : 8,
                  bgcolor: marker.color,
                  borderRadius: 1,
                  zIndex: 2,
                  boxShadow: (t) => `0 0 0 1.5px ${t.palette.background.paper}`,
                }}
              />
            </Tooltip>
          ),
        )}
      </Box>
    );
  }

  // One legend entry per cohort label, carrying its value so the number shows
  // next to the marker's colour.
  const refLegend = Array.from(
    new Map(
      refs.map((r) => {
        const { label, color } = resolveCohort(r.cohort);
        return [label, { color, value: r.value }] as const;
      }),
    ).entries(),
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.75,
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Box sx={{ width: { xs: 116, sm: 176 }, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>
          {prettyMetric(m.key)}
          {m.unit ? ` (${m.unit})` : ''}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {track ?? (
          <Typography variant="caption" color="text.secondary">
            No benchmark for this procedure yet.
          </Typography>
        )}
        {track && directional && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
              ‹ worse
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
              better ›
            </Typography>
          </Box>
        )}
        {(refLegend.length > 0 || you !== null || other !== null) && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
            {you !== null && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: YOU_COLOR, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" noWrap>You</Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: win === 'you' ? 'success.main' : 'text.primary' }}
                >
                  {fmtValue(you, m.unit)}
                </Typography>
              </Stack>
            )}
            {other !== null && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: OTHER_COLOR, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {otherLabel ?? 'Cohort'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: win === 'other' ? 'success.main' : 'text.primary' }}
                >
                  {fmtValue(other, m.unit)}
                </Typography>
              </Stack>
            )}
            {refLegend.map(([label, { color, value }]) => (
              <Stack key={label} direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {fmtValue(value, m.unit)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export function ParticipantCompareWelcome({
  tenantId,
  scaffold: initial,
}: {
  tenantId?: string;
  scaffold: ParticipantCompareScaffold;
}) {
  const hasData = initial.has_any_sessions;
  const [procedureId, setProcedureId] = useState(
    initial.procedures[0]?.id ?? '',
  );
  const [against, setAgainst] = useState('cohort');
  const procedureOptions = useMemo(
    () => initial.procedures.map((p) => ({ value: p.id, label: p.name })),
    [initial.procedures],
  );
  const [references, setReferences] = useState<
    Record<string, MetricReference[]>
  >(initial.references);
  const [data, setData] = useState<ParticipantCompareData | null>(null);
  const [loading, setLoading] = useState(false);

  // No sessions → just the selected procedure's reference cohorts (empty
  // state). With sessions → the self-vs-cohort data (you + comparison group +
  // references) for the selected procedure + comparison group.
  useEffect(() => {
    if (!procedureId) {
      setReferences({});
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    if (hasData) {
      getMyCompareData({ tenantId, procedureId, against })
        .then((d) => {
          if (cancelled) return;
          setData(d);
          setReferences(d.references);
        })
        .catch(() => {
          if (cancelled) return;
          setData(null);
          setReferences({});
        })
        .finally(() => !cancelled && setLoading(false));
    } else {
      getMyCompareScaffold({ tenantId, procedureId })
        .then((s) => !cancelled && setReferences(s.references))
        .catch(() => !cancelled && setReferences({}))
        .finally(() => !cancelled && setLoading(false));
    }
    return () => {
      cancelled = true;
    };
  }, [tenantId, procedureId, against, hasData]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      {initial.procedures.length > 0 && (
        <SearchableSelect
          id="cmp-welcome-proc"
          label="Procedure"
          size="small"
          sx={{ minWidth: 220, mb: 1, display: 'inline-flex', verticalAlign: 'top' }}
          value={procedureId}
          onChange={setProcedureId}
          options={procedureOptions}
        />
      )}

      {hasData && (
        <SearchableSelect
          id="cmp-welcome-against"
          label="Compare against"
          size="small"
          sx={{ minWidth: 200, mb: 1, ml: { sm: 1.5 }, display: 'inline-flex', verticalAlign: 'top' }}
          value={against}
          onChange={setAgainst}
          options={AGAINST_OPTIONS}
          keepOrder
        />
      )}

      {hasData && data?.other_below_threshold && (
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          Not enough participants in {data.other_label.toLowerCase()} yet to
          show a comparison (min {data.min_participants}). Your own values are
          shown against the benchmarks.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && initial.metrics.length === 0 && (
        <Alert severity="info" variant="outlined">
          No metrics are configured for your tenant yet.
        </Alert>
      )}

      {/* The "ready for your data" message lives with the metric cards — it's
          the empty state for the gauges below (benchmarks shown, your marker
          arrives once you record). Once the participant has sessions, it
          becomes a plain benchmarks header. */}
      {!loading && initial.metrics.length > 0 && !initial.has_any_sessions && (
        <Box
          sx={{
            mt: 1,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.primary.main, 0.25),
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
            We are ready for your data
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, maxWidth: 620, mx: 'auto' }}
          >
            These are the metrics we&apos;ll measure — and where practice-ready
            sits. Record a session and your marker appears on each gauge.
          </Typography>
        </Box>
      )}

      {!loading &&
        SECTIONS.map((section) => {
          const secMetrics = initial.metrics.filter(section.match);
          if (secMetrics.length === 0) return null;
          return (
            <Paper
              key={section.key}
              variant="outlined"
              sx={{
                mt: 2,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
              }}
            >
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, letterSpacing: '0.09em', color: 'primary.main' }}
              >
                {section.label}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {secMetrics.map((m) => (
                  <RefGauge
                    key={m.key}
                    m={m}
                    refs={references[m.key] ?? []}
                    you={data?.you[m.key] ?? null}
                    other={data?.other[m.key] ?? null}
                    otherLabel={data?.other_label}
                  />
                ))}
              </Box>
            </Paper>
          );
        })}
    </Paper>
  );
}
